import { Alert, AlertProps, Typography } from '@mui/material';
import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import SuccessIcon from '../../../../icons/svg/checkmark_circle.svg?react';
import ErrorIcon from '../../../../icons/svg/cross_circle.svg?react';
import InfoIcon from '../../../../icons/svg/info.svg?react';
import WarningIcon from '../../../../icons/svg/warning.svg?react';
import { FeedbackAlertAction } from './action';
import { FeedbackAlertTitle } from './title';

type Props = {
  title?: string;
  description: ReactNode;
  severity?: 'error' | 'info' | 'success' | 'warning';
  slim?: boolean;
  bulletPoints?: boolean;
  actionText?: string;
  actionCallback?: () => void;
  actionElement?: JSX.Element;
  customActionElement?: boolean;
} & Omit<AlertProps, 'children' | 'severity' | 'action'>;

export type FeedbackAlertProps = Props;

export const FeedbackAlert: FC<Props> = ({
  severity = 'success',
  description,
  title,
  role,
  slim = false,
  bulletPoints = true,
  onClose,
  actionText,
  actionCallback,
  actionElement,
  customActionElement
}) => {
  const { t } = useTranslation('common');
  const severityClassMap = {
    info: 'bg-info-lightest',
    warning: 'bg-warning-lightest',
    error: 'bg-error-lightest',
    success: 'bg-success-lightest'
  } as const;
  const severityColor = severityClassMap[severity];

  const renderDescription = () => {
    if (!Array.isArray(description)) {
      return (
        <Typography className="text-[14px]" variant="body2" marginY="2px">
          {description}
        </Typography>
      );
    }

    if (bulletPoints) {
      return (
        <ul style={{ paddingLeft: '20px', marginTop: '4px' }}>
          {description.map((desc) => (
            <li key={desc}>
              <Typography className="text-[14px]">{desc}</Typography>
            </li>
          ))}
        </ul>
      );
    }

    return (
      <div style={{ marginTop: '4px' }}>
        {description.map((desc) => (
          <Typography
            key={desc}
            className="text-[14px]"
            variant="body2"
            lineHeight={1.5}
            style={{ marginBottom: '8px' }}
          >
            {desc}
          </Typography>
        ))}
      </div>
    );
  };

  return (
    <Alert
      severity={severity}
      role={role}
      iconMapping={{
        success: <SuccessIcon />,
        error: <ErrorIcon />,
        info: <InfoIcon />,
        warning: <WarningIcon />
      }}
      onClose={onClose}
      closeText={t('CLOSE')}
      action={
        !customActionElement &&
        (actionText && actionCallback ? (
          <FeedbackAlertAction actionCallback={actionCallback} actionText={actionText} />
        ) : (
          actionElement
        ))
      }
      className={severityColor}
      sx={(theme) => ({
        paddingLeft: '12px',
        paddingRight: onClose ? '12px' : '16px',
        paddingY: slim ? '6px' : '12px',
        border: '1px solid',
        borderRadius: '12px',
        borderColor: theme.palette[severity].light,
        color: theme.palette.primary.main,
        '&>.MuiAlert-icon, & .MuiAlertTitle-root': {
          color: theme.palette[severity].main
        },
        '&>.MuiAlert-icon, & .MuiAlert-message': {
          paddingY: 0
        },
        '&>.MuiAlert-icon': {
          marginRight: '12px',
          '& svg': {
            width: '22px',
            height: '22px'
          }
        },
        '&>.MuiAlert-action': {
          marginLeft: '12px',
          marginRight: 0,
          padding: 0,
          '&>button.MuiIconButton-root': {
            padding: '4px'
          },
          '& svg.MuiSvgIcon-root': {
            fontSize: '1rem'
          }
        }
      })}
    >
      {title && <FeedbackAlertTitle title={title} />}
      {renderDescription()}

      {customActionElement &&
        (actionText && actionCallback ? (
          <FeedbackAlertAction actionCallback={actionCallback} actionText={actionText} />
        ) : (
          <div style={{ paddingTop: '12px' }}>{actionElement}</div>
        ))}
    </Alert>
  );
};
